import {
  isChrome,
  isChromium,
  isDesktop,
  isEdgeChromium,
  isOpera,
} from "react-device-detect";

import {
  BrowserExtensionIntent,
  ExtensionScorecardItem,
} from "../main/graphql";
import {
  AlertConfig,
  FillScorecardPayload,
  FillScorecardResponse,
} from "./oneClickScorecard";
import {
  FillScorecardV3CallDetails,
  FillScorecardV3Payload,
  FillScorecardV3ResponsePayload,
} from "./oneClickScorecard/v3";

const BROWSER_EXTENSION_ID = "mbokiighkhdopgedihndpibkincpcgan"; // cspell:disable-line
const BETA_BROWSER_EXTENSION_ID = "opnlcljcbpodfkedodibhbnegdkhappp"; // cspell:disable-line

enum MessageRejectReason {
  EXTENSION_NOT_SUPPORTED = "EXTENSION_NOT_SUPPORTED",
  EXTENSION_NOT_INSTALLED = "EXTENSION_NOT_INSTALLED",
}

export type MessageAction =
  | "getVersion"
  | "getTabId"
  | "openUrl"
  | "openIA"
  | "toggleDrawer"
  | "openDrawer"
  | "loadingComplete"
  | "getScorecardItems"
  | "fillScorecard"
  | "fillScorecardV3Ready"
  | "fillScorecardV3Start"
  | "fillScorecardV3Response"
  | "scorecardFilled"
  | "setPostFillAlert"
  | "showAlert"
  | "clearAlert"
  | "setToastText"
  | "disableDrawerToast"
  | "enableDrawerToast"
  | "openDrawerToast"
  | "closeDrawerToast"
  | "showModal"
  | "hideModal"
  | "pullScorecard"
  | "scorecardFeedback"
  | "showFeedback"
  | "fillCandidateNotes"
  | "pullCandidateNotes"
  | "candidateNotesStatus"
  | "gaEvent";

/** Summary template info for autofill options */
export interface AutofillSummaryTemplate {
  /** null represents BrightHire's default template */
  id: string | null;
  name: string;
  isDefault: boolean;
}

export interface MessagePayload {
  url?: string;
  tabId?: number;
  intent?: BrowserExtensionIntent;
  text?: string;
  toastHeight?: string;
  toastNotification?: string;
  scorecardData?: FillScorecardPayload;
  scorecardDataV3?: FillScorecardV3Payload;
  callDetails?: FillScorecardV3CallDetails;
  scorecardDataV3Response?: FillScorecardV3ResponsePayload;
  /** Available autofill summary templates */
  summaryTemplates?: AutofillSummaryTemplate[];
  alert?: AlertConfig;
  windowAvailHeight?: number;
  gaEvent?: any;
  isLoading?: boolean;
  /** Used for filling a JD on an ATS page */
  jobDescription?: { text?: string; html?: string };
  planQuestions?: { text: string }[];
  /** Scorecard v3 feedback data from ATS page */
  scorecardFeedback?: {
    rating: "positive" | "negative";
    comment?: string;
  };
  /** Formatted AI notes text to fill into a candidate notes field */
  candidateNotesText?: string;

  /** The URL of the ATS page that sent the message */
  tabUrl?: string;

  /** Whether the sidepanel has an interview selected for candidate notes import */
  hasInterviewSelected?: boolean;
}

export interface Message {
  action: MessageAction;
  payload?: MessagePayload;
  // deprecated properties
  // TODO: remove after next extension release
  openUrl?: string;
  iaUrl?: string;
}

export type MessageResponse =
  | {
      tabId?: number;
      version?: string;
      fillScorecardResult?: FillScorecardResponse;
      scorecardItems?: ExtensionScorecardItem[];
      ats?: string;
    }
  | null
  | undefined;

type MessagePort = chrome.runtime.Port;

/**
 * We use chrome's long-lived connections here to be
 * able to push events from the extension to the web app
 * https://developer.chrome.com/docs/extensions/mv3/messaging/#connect
 *
 * They are similar to chrome's one-time requests, but
 * they allow easier bi-directional messaging (since there is no
 * `runtime.onMessage` in this context / outside of the extension)
 */
let messagePort: MessagePort | null = null;
let initPromise: Promise<MessagePort> | undefined;
const messageHandlers = new Set<(message: Message) => void>();

const initMessagePort = async (): Promise<MessagePort> => {
  if (messagePort) return messagePort;
  if (initPromise) return initPromise;

  if (
    !(isDesktop && (isChrome || isEdgeChromium || isChromium || isOpera)) ||
    typeof chrome?.runtime === "undefined" // Extra check for older Safari (#4427)
  ) {
    return Promise.reject(MessageRejectReason.EXTENSION_NOT_SUPPORTED);
  }

  let removeInitialDisconnectListener: (() => void) | undefined;
  const onInitialDisconnect = (
    port: MessagePort,
    listener: () => void
  ): void => {
    const wrapped = () => {
      listener();
      port.onDisconnect.removeListener(wrapped);
    };
    port.onDisconnect.addListener(wrapped);

    removeInitialDisconnectListener = () =>
      port.onDisconnect.removeListener(wrapped);
  };

  initPromise = new Promise<MessagePort>((resolve, reject) => {
    const prodPort = chrome.runtime.connect(BROWSER_EXTENSION_ID);
    // The only way we can tell if the above failed is that the `onDisconnect` will
    // be fired right after `connect`, so we wait 100 ms (a guess) to allow for that
    const resolveProd = setTimeout(() => resolve(prodPort), 100);
    onInitialDisconnect(prodPort, () => {
      clearTimeout(resolveProd);
      if (chrome.runtime.lastError) {
        const betaPort = chrome.runtime.connect(BETA_BROWSER_EXTENSION_ID);
        onInitialDisconnect(betaPort, () => {
          if (chrome.runtime.lastError) {
            reject(MessageRejectReason.EXTENSION_NOT_INSTALLED);
          }
        });
        setTimeout(() => resolve(betaPort), 10);
      }
    });
  });
  const port = await initPromise.finally(() => {
    initPromise = undefined;
    removeInitialDisconnectListener?.();
  });

  if (messagePort) return messagePort;
  messagePort = port;

  messageHandlers.forEach((handler) => {
    messagePort?.onMessage.addListener(handler);
  });

  messagePort.onDisconnect.addListener(() => {
    messagePort = null;
    const shouldReconnect = messageHandlers.size > 0;
    if (shouldReconnect) {
      initMessagePort();
    }
  });

  return messagePort;
};

/**
 * Listens for messages sent from the extension using chrome's
 * [long lived connections](https://developer.chrome.com/docs/extensions/mv3/messaging/#connect)
 * API.
 */
export const onExtensionMessage = (
  onMessage: (message: Message) => void,
  onError?: (error: any) => void
): { stopListening(): void } => {
  let isStopped = false;

  initMessagePort()
    .then((messagePort) => {
      if (isStopped) return;
      if (!messagePort.onMessage.hasListener(onMessage)) {
        messageHandlers.add(onMessage);
        messagePort.onMessage.addListener(onMessage);
      }
    })
    .catch(onError);

  return {
    stopListening: () => {
      isStopped = true;
      messagePort?.onMessage.removeListener(onMessage);
      messageHandlers.delete(onMessage);
    },
  };
};

export const sendExtensionMessage = (
  message: Message
): Promise<MessageResponse> => {
  if (
    !(isDesktop && (isChrome || isEdgeChromium || isChromium || isOpera)) ||
    typeof chrome?.runtime === "undefined" // Extra check for older Safari (#4427)
  ) {
    return Promise.reject(MessageRejectReason.EXTENSION_NOT_SUPPORTED);
  }

  return new Promise((resolve, reject) => {
    // production extension get's priority when sending messages
    chrome.runtime.sendMessage(
      BROWSER_EXTENSION_ID,
      message,
      (response: MessageResponse) => {
        // if there was an error, try again with the beta extension ID
        if (chrome.runtime.lastError) {
          chrome.runtime.sendMessage(
            BETA_BROWSER_EXTENSION_ID,
            message,
            (betaResponse: MessageResponse) => {
              if (chrome.runtime.lastError) {
                reject(MessageRejectReason.EXTENSION_NOT_INSTALLED);
              } else {
                resolve(betaResponse);
              }
            }
          );
        } else {
          resolve(response);
        }
      }
    );
  });
};
